import type { ForecastPresentationHistoryDeal } from "@revelate/types/forecast-presentation";

const object = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") { try { return object(JSON.parse(value)); } catch { return {}; } }
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
};
const date = (value: unknown) => {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

/** Recover observed wins only; a planned close-date history or sync timestamp is not an actual win. */
export function recoverFutureHistoryDates(history: readonly ForecastPresentationHistoryDeal[],
  rawDeals: readonly { id: number; provider_data?: unknown }[], asOf: Date) {
  const raw = new Map(rawDeals.map(deal => [Number(deal.id), deal]));
  const recovered = new Map<string, { closedAt: Date }>();
  for (const deal of history) {
    const createdAt = date(deal.createdAt);
    const closedAt = date(deal.closedAt);
    if (!deal.isWon || !createdAt || createdAt >= asOf
      || closedAt && closedAt >= createdAt && closedAt < asOf) continue;
    const provider = object(raw.get(deal.id)?.provider_data);
    const candidate = date(object(provider.properties).closedate);
    if (candidate && candidate >= createdAt && candidate < asOf) {
      recovered.set(String(deal.id), { closedAt: candidate });
      continue;
    }
    const histories = object(provider.propertiesWithHistory);
    const entries = histories.hs_is_closed_won;
    if (!Array.isArray(entries)) continue;
    const ordered = entries.map(object).flatMap(record => {
      const at = date(record.timestamp);
      const value = record.value === true || record.value === "true" ? true
        : record.value === false || record.value === "false" ? false : null;
      return at && at < asOf && value !== null ? [{ at, value }] : [];
    }).sort((a, b) => a.at.getTime() - b.at.getTime());
    let wonAt: Date | null = null;
    let previous = false;
    for (const record of ordered) {
      if (record.value && !previous) wonAt = record.at;
      if (!record.value) wonAt = null;
      previous = record.value;
    }
    if (previous && wonAt && wonAt >= createdAt) recovered.set(String(deal.id), { closedAt: wonAt });
  }
  return recovered;
}
